import React, { useState } from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import type { IspCustomerRow, IspPlanRow } from '@/api/isp';

/**
 * Everything an operator needs to decide on one application.
 *
 * Ray, 2026-09-01: *"when approving it, it should show an action review details
 * because its important to see specially the ID."* Approving straight from a
 * table row means approving somebody whose ID you have not looked at — and the
 * ID is the whole reason the field exists.
 *
 * ─── THE ID IS THE POINT, SO IT IS BIG ───
 * Shown full width and clickable to open, not as a thumbnail. An operator is
 * comparing a name on a card to a name on a form; a 40px square cannot be read.
 *
 * ─── REJECTING NEEDS A REASON, APPROVING DOES NOT ───
 * A rejection the applicant cannot understand becomes a phone call. An approval
 * needs no explanation — it is what they asked for.
 */
const peso = (n: number) => `₱${Number(n ?? 0).toLocaleString()}`;

const when = (iso: string | null) =>
   iso
      ? new Date(iso).toLocaleDateString('en-PH', {
           day: 'numeric',
           month: 'long',
           year: 'numeric',
        })
      : '—';

interface Props {
   customer: IspCustomerRow;
   /** The plan they asked for on the public form, if it still exists. */
   wantedPlan: IspPlanRow | null;
   onCancel: () => void;
   onApprove: () => void;
   onReject: (reason: string) => void;
   busy?: boolean;
}

const IspApplicationReview: React.FC<Props> = ({
   customer,
   wantedPlan,
   onCancel,
   onApprove,
   onReject,
   busy,
}) => {
   const [rejecting, setRejecting] = useState(false);
   const [reason, setReason] = useState('');

   return (
      <Wrap>
         <Rows>
            <Row>
               <K>Name</K>
               <V>{customer.fullName}</V>
            </Row>
            <Row>
               <K>Mobile</K>
               <V>{customer.phone || '—'}</V>
            </Row>
            <Row>
               <K>Email</K>
               <V>{customer.email || '—'}</V>
            </Row>
            <Row>
               <K>Address</K>
               <V>{customer.address || '—'}</V>
            </Row>
            <Row>
               <K>Applied</K>
               <V>{when(customer.appliedAt)}</V>
            </Row>
            <Row>
               <K>Wants</K>
               <V>
                  {wantedPlan ? (
                     <>
                        {wantedPlan.name} · {peso(wantedPlan.price)}/month
                     </>
                  ) : (
                     // Said plainly rather than left blank: an operator seeing
                     // an empty cell wonders whether it failed to load.
                     <Muted>They did not pick one</Muted>
                  )}
               </V>
            </Row>
            {customer.notes ? (
               <Row>
                  <K>Notes</K>
                  <V>{customer.notes}</V>
               </Row>
            ) : null}
         </Rows>

         <IdBlock>
            <IdHead>
               <IdTitle>ID</IdTitle>
               <IdType>{customer.idType || 'Not stated'}</IdType>
            </IdHead>
            {customer.idPhotoUrl ? (
               <IdLink href={customer.idPhotoUrl} target="_blank" rel="noreferrer">
                  <IdImg src={customer.idPhotoUrl} alt="Customer ID" />
                  <IdOpen>Open full size</IdOpen>
               </IdLink>
            ) : (
               // 🔴 Never silent. An operator must know they are approving
               // somebody they cannot identify.
               <NoId>
                  No ID was uploaded. Ask for one before connecting them.
               </NoId>
            )}
         </IdBlock>

         {rejecting ? (
            <RejectBox>
               <Lbl htmlFor="reject-why">
                  Why are you turning this down?
               </Lbl>
               <Input
                  id="reject-why"
                  value={reason}
                  placeholder="e.g. Outside our coverage area"
                  onChange={(e) => setReason(e.target.value)}
               />
               <Hint>They will not see this — it is for your own records.</Hint>
               <Actions>
                  <Ghost type="button" onClick={() => setRejecting(false)}>
                     Back
                  </Ghost>
                  <Danger
                     type="button"
                     disabled={busy}
                     onClick={() => onReject(reason.trim())}
                  >
                     Reject application
                  </Danger>
               </Actions>
            </RejectBox>
         ) : (
            <Actions>
               <Ghost type="button" onClick={onCancel}>
                  Close
               </Ghost>
               <Danger type="button" onClick={() => setRejecting(true)}>
                  Reject
               </Danger>
               <Approve type="button" disabled={busy} onClick={onApprove}>
                  Approve &amp; set up connection
               </Approve>
            </Actions>
         )}
         {!rejecting ? (
            // Says where Approve leads. A button that silently opens another
            // form is a button people stop trusting.
            <Fine>
               Approving opens the connection form — nothing is written to your
               MikroTik until you save it there.
            </Fine>
         ) : null}
      </Wrap>
   );
};

const Wrap = styled.div`
   padding: 4px 2px;
`;
const Rows = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 2px 14px;
   margin-bottom: 16px;
`;
const Row = styled.div`
   display: flex;
   gap: 16px;
   padding: 10px 0;
   font-size: 13px;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;
const K = styled.span`
   width: 84px;
   flex: none;
   color: ${colors.GRAY5};
`;
const V = styled.span`
   font-weight: 600;
   overflow-wrap: anywhere;
`;
const Muted = styled.span`
   font-weight: 500;
   color: ${colors.GRAY5};
`;
const IdBlock = styled.div`
   margin-bottom: 16px;
`;
const IdHead = styled.div`
   display: flex;
   align-items: baseline;
   gap: 10px;
   margin-bottom: 8px;
`;
const IdTitle = styled.div`
   font-size: 12px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   color: ${colors.GRAY5};
`;
const IdType = styled.div`
   font-size: 13px;
   font-weight: 700;
`;
const IdLink = styled.a`
   display: block;
   text-decoration: none;
   color: ${colors.PRIMARY};
`;
const IdImg = styled.img`
   width: 100%;
   max-height: 320px;
   object-fit: contain;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   background: ${colors.GRAY14};
`;
const IdOpen = styled.div`
   margin-top: 6px;
   font-size: 12px;
   font-weight: 700;
   text-align: center;
`;
const NoId = styled.div`
   padding: 18px;
   border: 1px dashed ${colors.RED};
   border-radius: 10px;
   font-size: 13px;
   font-weight: 600;
   color: ${colors.RED};
   text-align: center;
`;
const RejectBox = styled.div`
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   padding: 14px;
`;
const Lbl = styled.label`
   display: block;
   font-size: 13px;
   font-weight: 700;
   margin-bottom: 6px;
`;
const Input = styled.input`
   width: 100%;
   box-sizing: border-box;
   font-family: inherit;
   font-size: 14px;
   padding: 10px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 9px;
   background: transparent;
   color: inherit;
`;
const Hint = styled.div`
   margin-top: 5px;
   font-size: 12px;
   color: ${colors.GRAY5};
`;
const Actions = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 8px;
   margin-top: 14px;
   flex-wrap: wrap;
`;
const btn = `
   font-family: inherit;
   font-size: 14px;
   font-weight: 700;
   padding: 10px 16px;
   border-radius: 9px;
   cursor: pointer;
`;
const Ghost = styled.button`
   ${btn}
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
`;
const Danger = styled.button`
   ${btn}
   border: 1px solid ${colors.RED};
   background: transparent;
   color: ${colors.RED};
`;
const Approve = styled.button`
   ${btn}
   border: none;
   background: ${colors.PRIMARY};
   color: #fff;
`;
const Fine = styled.p`
   margin: 10px 0 0;
   font-size: 12px;
   color: ${colors.GRAY5};
   text-align: right;
`;

export default IspApplicationReview;
